import { useAccountState } from '../../../redux/reducers/accountReducer';
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useFormik } from 'formik';
import {
    Accordion,
    AccordionHeader,
    AccordionItem,
    AccordionPanel,
    Button,
    Checkbox,
    Dropdown,
    Field,
    InfoLabel,
    Input,
    Link,
    makeStyles,
    mergeClasses,
    Option,
    OptionOnSelectData,
    SelectionEvents,
    Text,
    Textarea,
} from '@fluentui/react-components';
import { AddCircle16Regular, PuzzleCubePieceRegular } from '@fluentui/react-icons';
import { useAccess } from '../../../auth/useAccess';
import { useCaseDataService } from '../../../data/services';
import {
    CaseEventTemplate,
    CaseMilestoneTemplate,
    CaseStatus,
    CaseSystemStatus,
    CaseTemplate,
    useInitialValues,
} from '../../../data/types';
import { useEngagementTemplateService } from '../../../data/services/useEngagementTemplateService';
import { useCommonStyles } from '../../common/Styles/commonStyles';
import { Column, Paths, Row } from '../../common';
import { TemplatesTabs } from './TemplatesPage';
import { SettingsPageTitle } from '../Settings/SettingsPageTitle';
import { CaseTypeComboBox } from '../../controls/Comboboxes/CaseTypeComboBox';
import { AddEventTemplateDialog } from '../../dialogs/AddEventTemplateDialog';
import { LinkButton } from '../../controls/LinkButton';
import EventTemplateTable from './EventTemplateTable';
import { FormSection } from '../../common/FormSection';
import { AddEditCaseMilestoneTemplateDialog } from './AddEditCaseMilestoneTemplateDialog';
import { MilestoneTemplateTable } from './MilestoneTemplateTable';
import { useValidation } from '../../../data/types/validation';
import { appTokens } from '../../common/Styles/appTokens';

const useStyles = makeStyles({
    field: {
        width: '100%',
    },
});

type AddEditCaseTemplatePageProps = {
    templateToEdit?: CaseTemplate;
    onSubmit?: (template: CaseTemplate) => Promise<void>;
    onCancel?: () => void;
};
export const AddEditCaseTemplatePage = (props: AddEditCaseTemplatePageProps) => {
    const { templateToEdit, onSubmit, onCancel } = props;

    //Hooks
    const { primaryAccount } = useAccountState();
    const access = useAccess(primaryAccount?.user);
    const initValues = useInitialValues();
    const caseDataService = useCaseDataService();
    const caseStatuses = caseDataService.caseStatuses;
    const engagementTemplateService = useEngagementTemplateService();
    const navTo = useNavigate();
    const { validateCaseTemplate, validateFieldEntry, validateCaseObj, validateCaseNote } = useValidation();

    //Styles
    const commonStyles = useCommonStyles();
    const styles = useStyles();
    const fieldClasses = mergeClasses(styles.field, commonStyles.text.field);

    //Form
    const initTemplate = (): CaseTemplate => {
        if (!!templateToEdit) {
            return {
                ...initValues.caseTemplate(),
                ...templateToEdit,
            };
        }
        return initValues.caseTemplate();
    };
    const templateForm = useFormik<CaseTemplate>({
        initialValues: initTemplate() as CaseTemplate,
        onSubmit: async (values, { resetForm }) => {
            const caseTemplate = values as CaseTemplate;

            if (!!caseTemplate.templateName) {
                !!onSubmit && (await onSubmit(caseTemplate));

                resetForm({ values });
            }
        },
        validationSchema: validateCaseTemplate.schema,
    });

    //Logic
    const resetForm = () => {
        templateForm.resetForm({ values: initTemplate() });
    };
    const getNote = () => {
        return templateForm.values.notes?.[0] || '';
    };
    const getTemplateName = () => {
        return templateForm.values.engagementTemplate?.templateName || '';
    };

    //UI Events
    const onClickSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        templateForm.submitForm();
    };
    const onClickCancel = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        !!onCancel && onCancel();
    };

    //Case Type Logic
    const onCaseTypeSelected = (caseType?: string) => {
        templateForm.setFieldValue('caseType', caseType);
    };
    const routeToCaseData = async () => {
        await templateForm.submitForm();

        navTo(Paths.templates, { state: { tab: TemplatesTabs.caseData } });
    };

    //Case Status Logic
    const getStatusId = (stat: CaseStatus) => {
        return `${stat.systemStatus}:${stat.status}`;
    };
    const statusExistsIn = (stat: CaseStatus, statuses: CaseStatus[]) => {
        return !!statuses.find((s) => getStatusId(s) == getStatusId(stat));
    };
    const addStatus = (stat: CaseStatus) => {
        const statusArr = templateForm.values.statuses || [];
        if (!statusExistsIn(stat, statusArr)) {
            templateForm.setFieldValue('statuses', [...statusArr, stat]);
        }
    };
    const removeStatus = (stat: CaseStatus) => {
        const newStatuses = templateForm.values.statuses?.filter(
            (x) => getStatusId(x) != getStatusId(stat),
        );

        templateForm.setFieldValue('statuses', newStatuses);
    };
    const getStatusCheckboxChangeHandler = (stat: CaseStatus) => (_e: any, data: any) => {
        data.checked ? addStatus(stat) : removeStatus(stat);
    };

    //Engagement Template Logic
    const routeToEngagementTemplates = () => {
        navTo(Paths.templates, { state: { tab: TemplatesTabs.engagementTemplates } });
    };
    const selectEngagementTemplate = async (engTemplateId: string) => {
        const engTemplate =
            await engagementTemplateService.getEngagementTemplateById(engTemplateId);
        if (!!engTemplate) {
            templateForm.setFieldValue('engagementTemplate', engTemplate);
        }
    };
    const onEngagementTemplateOptionSelect = (_e: SelectionEvents, data: OptionOnSelectData) => {
        selectEngagementTemplate(data.optionValue as string);
    };

    //Event Template Logic
    const submitEventTemplate = async (evtTemplate: CaseEventTemplate) => {
        const events = [...(templateForm.values.events || [])];
        const index = events.findIndex((evt) => evt.id == evtTemplate.id);
        index >= 0 ? events.splice(index, 1, evtTemplate) : events.push(evtTemplate);
        templateForm.setFieldValue('events', events);
    };
    const removeEventTemplate = async (evtTemplate: CaseEventTemplate) => {
        console.log(evtTemplate);
        const events = templateForm.values.events?.filter((x) => x.id != evtTemplate.id);

        templateForm.setFieldValue('events', events);
    };

    //Milestone Template Logic
    const submitMilestoneTemplate = async (milestoneTemplate: CaseMilestoneTemplate) => {
        let milestones = [...(templateForm.values.milestones || [])];
        const index = milestones.findIndex((ms) => ms.id == milestoneTemplate.id);
        if (index >= 0) {
            milestones = [
                ...milestones.slice(0, index),
                milestoneTemplate,
                ...milestones.slice(index + 1),
            ];
        } else {
            milestones = [...milestones, milestoneTemplate];
        }

        templateForm.setFieldValue('milestones', milestones);
    };
    const removeMilestoneTemplate = async (milestoneTemplate: CaseMilestoneTemplate) => {
        const milestones = templateForm.values.milestones?.filter(
            (x) => x.id != milestoneTemplate.id,
        );

        templateForm.setFieldValue('milestones', milestones);
    };

    //Effects
    useEffect(() => {
        if (!!templateToEdit) {
            resetForm();
        }
    }, [templateToEdit]);

    return (
        <>
            <Column padding='px-3' flex='1' maxHeight={appTokens.pageHeight('6.25rem')}>
                <SettingsPageTitle
                    title='Add/Edit Case Template'
                    icon={<PuzzleCubePieceRegular style={{ fontSize: '1.625em' }} />}
                    action={
                        <Row gap='medium' padding='pr-3'>
                            <Button
                                appearance='primary'
                                onClick={onClickSubmit}
                                disabled={!templateForm.dirty || !templateForm.isValid}
                                data-testid='edit-case-template-save-button'
                            >
                                Save
                            </Button>
                            <Button appearance='secondary' onClick={onClickCancel} data-testid='edit-case-template-cancel-button'>
                                Cancel
                            </Button>
                        </Row>
                    }
                />

                <Column gap='large' padding='pr-2' scroll>
                    <Column gap='medium' height='auto'>
                        <Row>
                            <Column flex='1' minWidth='325px'>
                                <Field label='Template Name' className={fieldClasses} required validationMessage={templateForm.errors.templateName}>
                                    <Input
                                        name='templateName'
                                        value={templateForm.values.templateName}
                                        onChange={templateForm.handleChange}
                                        data-testid='edit-case-template-name-input'
                                    />
                                </Field>
                                <Field
                                    className={fieldClasses}
                                    label={
                                        <InfoLabel
                                            label='Case Type'
                                            info={
                                                <Column gap='medium'>
                                                    <Text size={200}>
                                                        Assign a specific case type for all cases created with this template. Choose from a list of
                                                        case types defined for your account.
                                                    </Text>
                                                    {access.settings.canEditCaseDataTypes() && (
                                                        <Text size={200}>
                                                            {' '}
                                                            <Link onClick={routeToCaseData}>
                                                                <Text size={200}>Click here</Text>
                                                            </Link>{' '}
                                                            to add/edit case types for your account.
                                                        </Text>
                                                    )}
                                                </Column>
                                            }
                                        />
                                    }
                                >
                                    <CaseTypeComboBox
                                        value={templateForm.values.caseType}
                                        onCaseTypeSelected={onCaseTypeSelected}
                                        routeToCaseTypes={routeToCaseData}
                                    />
                                </Field>
                            </Column>
                            <Column flex='1' minWidth='325px'>
                                <Field
                                    label='Template Description'
                                    className={fieldClasses}
                                    required
                                    validationMessage={templateForm.errors.templateDescription}
                                    hint={`${templateForm.values.templateDescription?.length || 0} / ${validateCaseTemplate.templateDescription.maxLength}`}
                                >
                                    <Textarea
                                        name='templateDescription'
                                        value={templateForm.values.templateDescription}
                                        onChange={templateForm.handleChange}
                                        rows={3}
                                        data-testid='edit-case-template-description-input'
                                        maxLength={validateCaseTemplate.templateDescription.maxLength}
                                    />
                                </Field>
                            </Column>
                        </Row>
                    </Column>
                    <Row>
                        <Column flex='1' minWidth='325px'>
                            <FormSection
                                title='Allowed Statuses'
                                height='28rem'
                                scroll
                                info={
                                    <Column gap='medium'>
                                        <Text size={200}>
                                            Assign the allowed case statuses for all cases created with this template. Choose from a list of case
                                            statuses defined for your account.
                                        </Text>
                                        {access.settings.canEditCaseDataTypes() && (
                                            <Text size={200}>
                                                <Link onClick={routeToCaseData} data-testid='edit-case-template-case-data-link'>
                                                    <Text size={200}>Click here</Text>
                                                </Link>{' '}
                                                to add/edit case statuses for your account.
                                            </Text>
                                        )}
                                    </Column>
                                }
                            >
                                <Column padding='px-1 pb-2'>
                                    <Column noGap>
                                        <Accordion collapsible>
                                            {Object.keys(CaseSystemStatus).map((sysStat) => (
                                                <React.Fragment key={sysStat}>
                                                    <AccordionItem value={CaseSystemStatus[sysStat as keyof typeof CaseSystemStatus]}>
                                                        <AccordionHeader>
                                                            {CaseSystemStatus[sysStat as keyof typeof CaseSystemStatus]}
                                                        </AccordionHeader>
                                                        <AccordionPanel>
                                                            <Column padding='pl-3' noGap>
                                                                {caseStatuses
                                                                    .filter(
                                                                        (x) =>
                                                                            x.systemStatus ==
                                                                            CaseSystemStatus[sysStat as keyof typeof CaseSystemStatus],
                                                                    )
                                                                    .map((stat: CaseStatus) => (
                                                                        <React.Fragment key={stat.status}>
                                                                            <Checkbox
                                                                                id={getStatusId(stat)}
                                                                                checked={statusExistsIn(stat, templateForm.values.statuses || [])}
                                                                                onChange={getStatusCheckboxChangeHandler(stat)}
                                                                                label={stat.status}
                                                                            />
                                                                        </React.Fragment>
                                                                    ))}
                                                            </Column>
                                                        </AccordionPanel>
                                                    </AccordionItem>
                                                </React.Fragment>
                                            ))}
                                        </Accordion>
                                    </Column>
                                </Column>
                            </FormSection>
                        </Column>
                        <Column flex='1' minWidth='325px'>
                            <FormSection
                                title='Description and Overview'
                                minHeight='100%'
                                info={
                                    <Column gap='medium'>
                                        <Text size={200}>Customize the 'Short Description' field for all cases created with this template.</Text>
                                        <Text size={200}>Also, give some common language to the Overview field.</Text>
                                    </Column>
                                }
                            >
                                <Column padding='px-1 pb-2'>
                                    <Field
                                        className={fieldClasses}
                                        label={
                                            <InfoLabel
                                                label={`Short Description Field`}
                                                info={
                                                    <Column gap='medium'>
                                                        <Text size={200}>
                                                            Give a name and default text to the 'Short Description' field for all cases created with
                                                            this template.
                                                        </Text>
                                                        <Text size={200}>
                                                            This field is used to give a short description or title for the case, or the action or
                                                            proceeding that this case is related to.
                                                        </Text>
                                                    </Column>
                                                }
                                            />
                                        }
                                    >
                                        <Column gap='small' padding='pl-3'>
                                            <Field label='Field Name' className={fieldClasses}>
                                                <Input
                                                    name='shortDescription.fieldName'
                                                    value={templateForm.values.shortDescription?.fieldName}
                                                    onChange={templateForm.handleChange}
                                                    maxLength={validateFieldEntry.fieldName.maxLength}
                                                    data-testid='edit-case-template-short-description-field-name-input'
                                                />
                                            </Field>
                                            <Field
                                                label='Default Text'
                                                className={fieldClasses}
                                                hint={`${templateForm.values.shortDescription?.fieldValue?.length || 0} / ${validateCaseObj.shortDescription.maxLength}`}
                                            >
                                                <Textarea
                                                    name='shortDescription.fieldValue'
                                                    value={templateForm.values.shortDescription?.fieldValue}
                                                    onChange={templateForm.handleChange}
                                                    data-testid='edit-case-template-short-description-field-value-input'
                                                    placeholder='Default text'
                                                    rows={2}
                                                    maxLength={validateCaseObj.shortDescription.maxLength}
                                                />
                                            </Field>
                                        </Column>
                                    </Field>
                                    <Field
                                        className={fieldClasses}
                                        label={
                                            <InfoLabel
                                                label='Overview'
                                                info={
                                                    <Column noGap>
                                                        <Text size={200}>
                                                            Craft an initial overall description of cases created with this template.
                                                        </Text>
                                                    </Column>
                                                }
                                            />
                                        }
                                        hint={`${templateForm.values.overview?.length || 0} / ${validateCaseTemplate.overview.maxLength}`}
                                    >
                                        <Textarea
                                            name='overview'
                                            value={templateForm.values.overview}
                                            onChange={templateForm.handleChange}
                                            placeholder='Default text'
                                            rows={5}
                                            maxLength={validateCaseTemplate.overview.maxLength}
                                            data-testid='edit-case-template-overview-input'
                                        />
                                    </Field>
                                </Column>
                            </FormSection>
                        </Column>
                    </Row>

                    <Row>
                        <Column flex='1' minWidth='325px'>
                            <FormSection
                                title='Initial Note'
                                minHeight='100%'
                                info={
                                    <Column noGap>
                                        <Text size={200}>Quickly add an initial note to a new case created from this template.</Text>
                                    </Column>
                                }
                            >
                                <Field className={fieldClasses} label='Note' hint={`${getNote().length}/${validateCaseNote.text.maxLength}`}>
                                    <Textarea
                                        name='notes.0'
                                        rows={3}
                                        value={`${getNote()}`}
                                        onChange={templateForm.handleChange}
                                        placeholder='Add initial note'
                                        maxLength={validateCaseNote.text.maxLength}
                                        data-testid='edit-case-template-initial-note-input'
                                    />
                                </Field>
                            </FormSection>
                        </Column>

                        <Column flex='1' minWidth='325px'>
                            <FormSection
                                title='Engagement Template'
                                minHeight='100%'
                                info={
                                    <Column gap='medium'>
                                        <Text size={200}>
                                            Easily create the Engagement document for a new case created with this template by selecting from a list
                                            of previously created Engagement Templates.
                                        </Text>
                                        <Text size={200}>
                                            <Link onClick={routeToEngagementTemplates} data-testid='edit-case-template-engagement-templates-link'>
                                                <Text size={200}>Click here</Text>
                                            </Link>{' '}
                                            to create/edit Engagement Templates.
                                        </Text>
                                    </Column>
                                }
                            >
                                <Field className={fieldClasses} label='Select Template'>
                                    <Dropdown
                                        value={`${getTemplateName()}`}
                                        selectedOptions={[templateForm.values.engagementTemplate?.templateId || '']}
                                        onOptionSelect={onEngagementTemplateOptionSelect}
                                        data-testid='edit-case-template-engagement-template-dropdown'
                                    >
                                        {engagementTemplateService.engagementTemplates.map((engTemplate) => (
                                            <Option
                                                key={engTemplate.templateId}
                                                value={engTemplate.templateId || ''}
                                                text={engTemplate.templateName || ''}
                                                data-testid={`edit-case-template-engagement-template-option-${engTemplate.templateName}`}
                                            >
                                                {engTemplate.templateName}
                                            </Option>
                                        ))}
                                    </Dropdown>
                                </Field>
                            </FormSection>
                        </Column>
                    </Row>

                    <FormSection
                        title='Milestones'
                        info={
                            <Column gap='medium'>
                                <Text size={200}>
                                    Create common Milestones for the selected case type that will be applied to each case created with this template.
                                </Text>
                            </Column>
                        }
                        action={
                            <AddEditCaseMilestoneTemplateDialog onSubmit={submitMilestoneTemplate}>
                                <LinkButton
                                    text='Add Milestone'
                                    icon={<AddCircle16Regular />}
                                    data-testid='edit-case-template-add-milestone-button'
                                />
                            </AddEditCaseMilestoneTemplateDialog>
                        }
                    >
                        <Column width='99%' scroll padding='pb-2'>
                            <MilestoneTemplateTable
                                bodyHeight={`${Math.min((templateForm.values.milestones?.length || 0) * 4, 25)}rem`}
                                milestoneTemplates={templateForm.values.milestones}
                                onSubmit={submitMilestoneTemplate}
                                onDelete={removeMilestoneTemplate}
                            />
                        </Column>
                    </FormSection>

                    <FormSection
                        title='Events'
                        info={
                            <Column noGap>
                                <Text size={200}>
                                    Add common events that should be created for any case created from this template, especially events that may not
                                    be directly connected to a Milestone.
                                </Text>
                            </Column>
                        }
                        action={
                            <AddEventTemplateDialog onSubmit={submitEventTemplate}>
                                <LinkButton text='Add Event' icon={<AddCircle16Regular />} data-testid='edit-case-template-add-event-button' />
                            </AddEventTemplateDialog>
                        }
                    >
                        {/* Add EventTemplate table with add EventTemplate dialog */}
                        <EventTemplateTable
                            bodyHeight={`${Math.min((templateForm.values.events?.length || 0) * 4, 25)}rem`}
                            eventTemplates={templateForm.values.events}
                            onSubmit={submitEventTemplate}
                            onDelete={removeEventTemplate}
                        />
                    </FormSection>
                </Column>
            </Column>
        </>
    );
};
